/**
 * What the home screen's expense sheet opens on: the group and the category
 * this person used last.
 *
 * The home screen is the one door into the app, and until now everything you
 * could *do* was behind a group — so filing "$8.40, coffee" started with
 * finding the group it belongs in, every single time, even though it is the
 * same group as yesterday and the day before. This module is the answer to
 * "which group, and what for", so that the sheet on the home screen opens
 * already pointed at the ordinary case and the whole of filing an expense is
 * typing the amount.
 *
 * **It is a suggestion and never a decision.** Everything it fills in is a
 * control the person is looking at — the group picker above the sheet, the
 * category row inside it — and the sheet has a Reset beside it that puts it
 * all back to blank. Nothing here is ever sent anywhere: `resolveSheet` is
 * still what decides what a sendable expense is, and the server still decides
 * whether it may be written.
 *
 * ## Where the answer comes from, in order
 *
 * 1. **What this browser last filed**, in `localStorage`. It is written when
 *    an expense is actually filed — from the home screen, from a group, from
 *    a receipt — and it is the only source that knows the *category*, because
 *    nothing the API sends the home screen carries one: `/api/activity`'s rows
 *    hold a description and an amount, and `/api/groups` holds no expenses at
 *    all.
 * 2. **The group your activity feed is newest in.** This is what a second
 *    device, or a browser whose storage has been cleared, falls back to — the
 *    home screen has already asked for that feed, so it costs no request. It
 *    is second and not first because "the group something happened in" is a
 *    weaker claim than "the group you filed into": somebody else adding a
 *    member moves a group up this list, and filing an expense is the thing
 *    this is trying to predict.
 * 3. **The only group there is.** Not covered by the feed when the feed is
 *    empty, which is exactly the state a brand new group is in — and a group
 *    with nothing in it yet is the one somebody is most likely about to file
 *    into.
 *
 * If none of those lands there is no suggestion, the picker opens on nothing
 * and the person chooses. That is honest: with four groups and nothing
 * remembered there is no such thing as the most recent one, and guessing
 * would put an expense in the wrong group quietly, which is the failure this
 * whole feature must not have.
 *
 * It is pure — no `window`, no React — so the rule is testable as what it is,
 * a function from what is remembered and what is on the screen to a group id.
 * `src/last-expense.ts` is the two lines that reach for the real
 * `localStorage`.
 */

import { isCategory } from './categories'

export const LAST_EXPENSE_STORAGE_KEY = 'friendloan.last-expense'

/** What is remembered, and all of it: which group, and what for. */
export type LastExpense = {
  groupId: string
  /** A `src/lib/categories.ts` id, or `null` for uncategorised. */
  category: string | null
}

/** The three methods of `localStorage` this uses, and null for "there isn't one". */
export type LastExpenseStorage = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
} | null

/**
 * What this browser last filed, if it is still something this build can mean.
 *
 * Everything about the stored value is treated as untrusted — it is JSON in a
 * place a person can edit, written by whatever version of this app they had
 * open last — so a group id has to be a non-empty string and a category has to
 * be one this build knows. A category from a newer deploy is dropped rather
 * than carried: `CategoryPicker` would not draw it, so it would be a category
 * that is set, invisible, and refused by the API at the end of it.
 */
export function readLastExpense(storage: LastExpenseStorage): LastExpense | null {
  let raw: string | null
  try {
    raw = storage?.getItem(LAST_EXPENSE_STORAGE_KEY) ?? null
  } catch {
    return null
  }
  if (raw === null) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }

  if (typeof parsed !== 'object' || parsed === null) return null
  const record = parsed as Record<string, unknown>
  const groupId = record.groupId
  if (typeof groupId !== 'string' || groupId === '') return null

  return { groupId, category: isCategory(record.category) ? record.category : null }
}

/**
 * Remembers a filing. Storage being full, disabled or blocked costs the next
 * load its pre-fill and nothing else, so it is swallowed rather than surfaced
 * — the same trade `rememberTheme` makes.
 */
export function rememberLastExpense(last: LastExpense, storage: LastExpenseStorage): void {
  try {
    storage?.setItem(
      LAST_EXPENSE_STORAGE_KEY,
      JSON.stringify({
        groupId: last.groupId,
        category: isCategory(last.category) ? last.category : null,
      }),
    )
  } catch {
    /* not worth telling anybody about */
  }
}

/** Only for the tests and for a browser being put back to how it was found. */
export function forgetLastExpense(storage: LastExpenseStorage): void {
  try {
    storage?.removeItem(LAST_EXPENSE_STORAGE_KEY)
  } catch {
    /* as above */
  }
}

/**
 * A group as this module needs to read one — the fields of `GroupSummary`
 * that decide whether an expense may be filed into it.
 */
export type ChoosableGroup = {
  id: string
  archivedAt: string | null
  you: { leftAt: string | null }
}

/**
 * The groups the home screen may offer.
 *
 * An archived group takes no writes (`server/transactions.ts` refuses one) and
 * a group you have left is not yours to file into, so neither belongs in a
 * picker — offering them would mean pre-filling a sheet whose Add expense
 * button can only ever fail. Both stay on the home screen's own list below,
 * because their money still counts.
 */
export function filableGroups<T extends ChoosableGroup>(groups: readonly T[]): T[] {
  return groups.filter((group) => group.archivedAt === null && group.you.leftAt === null)
}

/** One row of the home screen's feed, as far as this is concerned. */
export type FeedGroup = { groupId: string }

/**
 * Which group the sheet opens on, and what for.
 *
 * The order is the one this module's own comment sets out: what was filed
 * here, then what the feed is newest in, then the only group there is. The
 * feed is taken in the order it arrives — `GET /api/activity` is newest
 * first — rather than re-sorted, for the reason the group screen does not
 * re-sort its expenses: the server has already answered that question.
 *
 * The category survives a group that did not. If the group somebody last
 * filed into has since been archived, "Groceries" is still the last thing
 * they filed, and it is one tap to unset — whereas dropping it would mean the
 * pre-fill silently getting worse for a reason that has nothing to do with it.
 */
export function suggestedExpense(options: {
  /** What `readLastExpense` gave back, or nothing. */
  remembered: LastExpense | null
  /** Every group the home screen has, unfiltered — this does the filtering. */
  groups: readonly ChoosableGroup[]
  /** The activity feed, newest first. Empty when it has not arrived yet. */
  feed?: readonly FeedGroup[]
}): LastExpense | null {
  const choosable = filableGroups(options.groups)
  if (choosable.length === 0) return null

  const ids = new Set(choosable.map((group) => group.id))
  const category = options.remembered?.category ?? null

  if (options.remembered !== null && ids.has(options.remembered.groupId)) {
    return { groupId: options.remembered.groupId, category }
  }

  const recent = (options.feed ?? []).find((entry) => ids.has(entry.groupId))
  if (recent !== undefined) return { groupId: recent.groupId, category }

  if (choosable.length === 1) return { groupId: choosable[0].id, category }

  return null
}

/**
 * What Reset puts the sheet back to.
 *
 * "Blank" is a sheet with no category and — where there is a choice to make —
 * no group either, so that somebody whose last expense is a bad starting
 * point is not left correcting it field by field. Where there is only one
 * group to file into, keeping it selected is not a pre-fill anybody has to
 * undo: it is the only answer, and clearing it would cost a tap to put back
 * the same value.
 *
 * It does not forget what is in storage. Reset is about the sheet in front of
 * somebody, not about the device's memory of what they do most days; the next
 * expense they file is what changes that.
 */
export function blankExpense(groups: readonly ChoosableGroup[]): LastExpense | null {
  const choosable = filableGroups(groups)
  if (choosable.length === 1) return { groupId: choosable[0].id, category: null }
  return null
}
